import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { observer } from 'mobx-react-lite';
import CustomTextField from 'shared/lib/components/controls/textFields/customTextField';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import Modal, { ModalProps } from 'shared/lib/components/modals/modal';
import { useTranslation } from 'react-i18next';
import { emptySelectItemId } from 'shared/lib/utils/globalUtils';
import PlacementSelection from 'widgets/selectPlacement/placementSelection';
import { InitialPlacement, PlacementParams } from 'entities/types';

export default observer(function EditTableModal({
  initialFolderId,
  initialSectionId,
  onSubmit,
  onDelete,
  applyText,
  headerText,
  tableName,
  handleClose,
  isOpen,
  sx,
}: InitialPlacement &
  ModalProps & {
    onSubmit: (params: PlacementParams) => Promise<any>;
    onDelete: () => void;
    applyText?: string;
    headerText?: string;
    tableName: string;
  }) {
  const { t } = useTranslation();
  applyText = applyText || t('common.save');
  headerText = headerText || t('common.edit');

  const {
    spacings: { sp },
  } = useContext(UiContext);

  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState(tableName);

  const [selectedFolderId, setSelectedFolderId] = useState<string>(
    initialFolderId || ''
  );
  const [selectedSectionId, setSelectedSectionId] = useState<string>(
    initialSectionId || ''
  );

  const isDisabled = selectedSectionId === emptySelectItemId;

  const finalIsDisabled = useMemo(() => {
    const changes =
      name !== tableName ||
      selectedFolderId !== (initialFolderId || '') ||
      selectedSectionId !== (initialSectionId || '');

    return !changes || isLoading || isDisabled || name.trim().length === 0;
  }, [
    name,
    tableName,
    selectedFolderId,
    initialSectionId,
    selectedSectionId,
    initialFolderId,
    isLoading,
    isDisabled,
  ]);

  const handleLoadingChange = (isLoading: boolean) => {
    setIsLoading(isLoading);
  };

  return (
    <Modal isOpen={isOpen} handleClose={handleClose}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp.s60 }}>
        <Typography variant="f24">{headerText}</Typography>
        <Box
          sx={{
            display: 'flex',
            position: 'relative',
            flexDirection: 'column',
            gap: sp.s40,
            overflow: 'hidden',
            ...sx,
          }}
        >
          <CustomTextField
            label={t('formFields.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            isClearIcon={true}
            labelTypographyVariantSize="f14"
            labelSxProps={{
              color: 'text.secondary',
            }}
          />

          <Paper
            elevation={4}
            sx={{
              display: 'flex',
              position: 'relative',
              flexDirection: 'column',
              overflow: 'hidden',
              width: 1,
              borderRadius: '16px',
              gap: sp.s40,
              p: sp.s60,
            }}
          >
            <PlacementSelection
              initialSectionId={selectedSectionId}
              initialFolderId={selectedFolderId || ''}
              onChangeFolder={(folderId) => setSelectedFolderId(folderId || '')}
              onChangeSection={(sectionId) => setSelectedSectionId(sectionId)}
              labelProps={{
                textFolder: t('folder.folder'),
                textSection: t('section.section'),
                sx: { color: 'text.secondary' },
              }}
              onLoadingChange={handleLoadingChange}
            />
          </Paper>
        </Box>
        <ButtonsContainer variant="vertical" sx={{ width: 1 }}>
          <ButtonsContainer>
            <SecondaryButton onClick={handleClose}>
              {t('common.cancel')}
            </SecondaryButton>
            <PrimaryButton
              loading={isLoading}
              disabled={finalIsDisabled}
              onClick={async () => {
                setIsLoading(true);

                try {
                  await onSubmit({
                    folderId: selectedFolderId,
                    sectionId: selectedSectionId,
                    name,
                  });
                } catch (e) {
                  console.error(e);
                }

                setIsLoading(false);
              }}
            >
              {applyText}
            </PrimaryButton>
          </ButtonsContainer>

          {onDelete ? (
            <PrimaryButton
              onClick={() => onDelete()}
              variant="contained"
              color="error"
            >
              {t('common.delete')}
            </PrimaryButton>
          ) : (
            ''
          )}
        </ButtonsContainer>
      </Box>
    </Modal>
  );
});
